import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import { Box, Button, Card, IconButton, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../../components/CustomButton";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import {
  deleteProcedureDetailByIdWoLoader,
  getPetsCompliantSummaryWoLoader,
  updateNoteByAppointmentIdWoLoader,
  updateProcedureByIdWoLoader,
} from "../../../../../redux/reducers/petSlice";
import { AppColors } from "../../../../../util/AppColors";
import AddAdministerDrawer from "./AddAdministerDrawer";
import AdministerCard from "./AdministerCard";
import EditAdministerDrawer from "./EditAdministerDrawer";

const addIcon = { color: AppColors.appPrimary, width: 15, height: 15 };

const AdministerSection = ({ memoOnly = false }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isAddDrawerOpen, setIsAddDrawerOpen] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [delModVsble, setDelModVsble] = useState(false);
  const [deleteDetailId, setDeleteDetailId] = useState(null);
  const [addMemoVisible, setAddMemoVisible] = useState(false);
  const [memo, setMemo] = useState("");
  const [isMemoEdit, setIsMemoEdit] = useState(false);
  const [isSavingMemo, setIsSavingMemo] = useState(false);
  const [togglingDetailId, setTogglingDetailId] = useState(null);
  const reqDet = useSelector((state) => state?.pet?.complaintSummary?.data);
  const appDet = reqDet?.appointment;

  useEffect(() => {
    const existingNotes = appDet?.notes ?? "";
    if (existingNotes?.length > 0) {
      setAddMemoVisible(true);
      setMemo(existingNotes);
      setIsMemoEdit(false);
    } else if (!isMemoEdit) {
      setMemo("");
    }
  }, [appDet?.notes, appDet?.appointmentId]);

  const handleOpenAddDrawer = () => {
    setIsAddDrawerOpen(true);
  };

  const handleOpenEditDrawer = (item) => {
    setSelectedItem(item);
    setIsEditDrawerOpen(true);
  };

  const onCloseDelMod = () => {
    setDelModVsble(false);
    setDeleteDetailId(null);
  };

  const handleDeleteItem = (detailId) => {
    if (!detailId) return;
    setDeleteDetailId(detailId);
    setDelModVsble(true);
  };

  const onConfirmDelete = () => {
    if (!deleteDetailId) return;
    dispatch(deleteProcedureDetailByIdWoLoader(deleteDetailId)).then((res) => {
      if (res?.payload) {
        onCloseDelMod();
        if (appDet?.appointmentId) {
          dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
        }
      }
    });
  };

  const handleSaveMemo = () => {
    if (!appDet?.appointmentId) return;

    setIsSavingMemo(true);
    dispatch(
      updateNoteByAppointmentIdWoLoader({
        id: appDet?.appointmentId,
        note: memo ?? "",
        remarks: appDet?.remarks,
      }),
    ).then((res) => {
      setIsSavingMemo(false);
      if (res?.payload) {
        setIsMemoEdit(false);
        dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
      }
    });
  };

  const handleToggleAdministered = (item) => {
    const reqProced = item?.procedure;
    if (!item || !reqProced?.procedureId || togglingDetailId) return;

    const { procedure, ...detail } = item;
    const reqObj = {
      ...detail,
      status: true,
      remarks: "Requested",
      isDelete: false,
      patientWorkbook: true,
    };

    setTogglingDetailId(item?.detailId ?? item?.id ?? true);
    dispatch(
      updateProcedureByIdWoLoader({
        id: reqProced?.procedureId,
        data: {
          serviceCharge: reqProced?.serviceCharge,
          serviceType: reqProced?.serviceType,
          appointmentId: reqDet?.appointment?.appointmentId,
          procedureDetails: [reqObj],
          tax: Number(reqProced?.tax ?? 0),
          total: Number(reqProced?.total ?? 0),
          mrpPrice: Number(reqProced?.mrpPrice ?? 0),
          procedureVariantId: reqProced?.procedureVariantId,
          quantity: Number(reqProced?.quantity ?? 1),
          hsnSacCode: reqProced?.hsnSacCode ?? null,
          paymentId: reqDet?.paymentId ?? null,
        },
      }),
    ).then((res) => {
      setTogglingDetailId(null);
      if (res?.payload && appDet?.appointmentId) {
        dispatch(getPetsCompliantSummaryWoLoader(appDet?.appointmentId));
      }
    });
  };

  const getAdministerList = () => {
    const reqList = reqDet?.procedures ?? [];
    return reqList
      ?.flatMap((procedure) =>
        (procedure?.procedureDetails ?? [])
          ?.filter((detail) => detail?.type === "Administered")
          ?.map((detail) => ({ ...detail, procedure })),
      )
      ?.sort((a, b) => a.detailId - b.detailId);
  };

  return (
    <Box sx={{ width: "100%", mt: 2, mb: 4 }}>
      <Card
        sx={{
          borderRadius: "8px",
          boxShadow: "0px 1px 4px rgba(0, 0, 0, 0.08)",
          border: "1px solid #e0e0e0",
          backgroundColor: "#ffffff",
          overflow: "hidden",
        }}
      >
        <Box
          onClick={() => setIsExpanded(!isExpanded)}
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2.5,
            py: 1.8,
            cursor: "pointer",
            backgroundColor: "#ffffff",
            userSelect: "none",
            "&:hover": {
              backgroundColor: "#fafafa",
            },
          }}
        >
          <Typography
            sx={{
              fontWeight: 700,
              color: AppColors.appPrimary,
              fontSize: "16px",
            }}
          >
            {memoOnly
              ? "Memo"
              : `Administer (${getAdministerList()?.length})`}
          </Typography>

          <IconButton size="small" sx={{ color: AppColors.appPrimary }}>
            {isExpanded ? <ArrowDropDownIcon /> : <ArrowRightIcon />}
          </IconButton>
        </Box>

        {isExpanded && (
          <Box px={2.5} pb={2.5} pt={1}>
            {!memoOnly ? (
              <Box mb={2}>
                {getAdministerList()?.map((item) => (
                  <AdministerCard
                    key={item.detailId}
                    item={item}
                    onEdit={handleOpenEditDrawer}
                    onDelete={handleDeleteItem}
                    onToggleAdministered={handleToggleAdministered}
                    isToggling={
                      togglingDetailId === (item?.detailId ?? item?.id)
                    }
                  />
                ))}
                {getAdministerList()?.length === 0 && (
                  <Typography sx={{ fontSize: "14px", color: "#9e9e9e", py: 2 }}>
                    No administered items added yet.
                  </Typography>
                )}
              </Box>
            ) : null}

            {addMemoVisible ? (
              <Box mb={2}>
                <CustomTextField
                  fullWidth
                  value={memo ?? ""}
                  handleChange={(e) => {
                    setMemo(e?.target?.value);
                    setIsMemoEdit(true);
                  }}
                  multiline
                  placeholder="Memo"
                  expandWithContent
                  disabled={!isMemoEdit}
                />
              </Box>
            ) : null}

            {!memoOnly ? (
              <Box
                onClick={handleOpenAddDrawer}
                sx={{
                  border: "1.5px dashed #90caf9",
                  borderRadius: "8px",
                  backgroundColor: "#f8faff",
                  py: 1.5,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "#eef4ff",
                    borderColor: AppColors.appPrimary,
                  },
                }}
              >
                <Button
                  startIcon={<AddIcon sx={{ fontSize: 20 }} />}
                  sx={{
                    color: AppColors.appPrimary,
                    fontWeight: 700,
                    fontSize: "14px",
                    textTransform: "none",
                    pointerEvents: "none",
                  }}
                >
                  Add item
                </Button>
              </Box>
            ) : null}

            <Box display="flex" alignItems="center" mt={2}>
              <Box
                display="flex"
                alignItems="center"
                onClick={() => {
                  setAddMemoVisible(true);
                  setIsMemoEdit(true);
                }}
                sx={{ cursor: "pointer" }}
              >
                <Box className="flex-center plus-icon">
                  <AddIcon sx={addIcon} />
                </Box>
                <Typography className="blue-color fs16 ml5 font-bold">
                  Add Memo
                </Typography>
              </Box>

              {isMemoEdit ? (
                <Box ml={2.5}>
                  <CustomButton
                    text="Save"
                    smallBtn
                    disabled={isSavingMemo}
                    onClick={handleSaveMemo}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>
        )}
      </Card>

      {!memoOnly ? (
        <>
          <AddAdministerDrawer
            open={isAddDrawerOpen}
            onClose={() => setIsAddDrawerOpen(false)}
          />

          <EditAdministerDrawer
            open={isEditDrawerOpen}
            onClose={() => setIsEditDrawerOpen(false)}
            item={selectedItem}
          />

          <CustomModal
            open={delModVsble}
            onClose={onCloseDelMod}
            header=""
            modal
            modalWidth={30}
            headerShown={false}
          >
            <div className="flex-center">
              <Typography className="txt-semi-bold fs16 text-align-center mv20 orange4">
                Are you sure you want to delete this record?
              </Typography>
            </div>
            <div className="flex-row-ali-cen flex-center pb20">
              <div>
                <CustomButton
                  text="Yes"
                  redBtn
                  smallBtn
                  onClick={onConfirmDelete}
                />
              </div>
              <div className="ml20">
                <CustomButton text="No" smallBtn onClick={onCloseDelMod} />
              </div>
            </div>
          </CustomModal>
        </>
      ) : null}
    </Box>
  );
};

export default AdministerSection;
